import { AlertTriangle, ShieldCheck, ShieldAlert, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";

const COLORS = {
    green: { tone: "var(--brand-success)", chip: "rgba(46,125,50,0.12)" },
    amber: { tone: "var(--brand-warning)", chip: "rgba(237,108,2,0.12)" },
    red: { tone: "var(--brand-red)", chip: "rgba(192,28,33,0.12)" },
};

function severity(n) {
    if (!n || n === 0) return "green";
    if (n <= 3) return "amber";
    return "red";
}

function ComplianceCard({ icon: Icon, label, count, sub, link, testId }) {
    const sev = severity(count);
    const palette = COLORS[sev];
    return (
        <Link
            to={link}
            className="brand-card flex flex-col gap-3 transition-transform hover:-translate-y-0.5"
            style={{
                borderLeft: `4px solid ${palette.tone}`,
                background: "var(--brand-bg, #fff)",
            }}
            data-testid={testId}
        >
            <div className="flex items-start gap-3">
                <div
                    className="flex items-center justify-center rounded-md shrink-0"
                    style={{
                        width: 36,
                        height: 36,
                        background: palette.chip,
                        color: palette.tone,
                    }}
                >
                    <Icon size={18} />
                </div>
                <div className="min-w-0 flex-1">
                    <p
                        className="eyebrow"
                        style={{ color: "var(--brand-muted)" }}
                        data-testid={`${testId}-label`}
                    >
                        {label}
                    </p>
                    <p
                        className="font-display"
                        style={{
                            fontSize: "1.85rem",
                            fontWeight: 800,
                            lineHeight: 1.05,
                            color: palette.tone,
                            marginTop: "0.25rem",
                        }}
                        data-testid={`${testId}-value`}
                    >
                        {count ?? 0}
                    </p>
                    <p className="text-muted text-xs mt-1">{sub}</p>
                </div>
            </div>
        </Link>
    );
}

export default function ComplianceTile({ metrics }) {
    const overdue = metrics?.overdue_training_this_week ?? 0;
    const expiring = metrics?.expiring_insurance_30d ?? 0;
    const pending = metrics?.pending_onboarding ?? 0;

    return (
        <section
            className="mt-8 fade-up"
            data-testid="compliance-tile"
            aria-label="Compliance risk overview"
        >
            <div className="flex items-baseline justify-between flex-wrap gap-2">
                <p className="eyebrow">Compliance risk</p>
                <p className="text-xs text-muted">
                    Green = clear · Amber = 1-3 · Red = 4+
                </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
                <ComplianceCard
                    icon={AlertTriangle}
                    label="Overdue training"
                    count={overdue}
                    sub="Assignments due this week, still incomplete"
                    link="/admin/training"
                    testId="compliance-overdue-training"
                />
                <ComplianceCard
                    icon={ShieldAlert}
                    label="Insurance expiring"
                    count={expiring}
                    sub="Contractor COIs within 30 days"
                    link="/admin/onboarding"
                    testId="compliance-expiring-insurance"
                />
                <ComplianceCard
                    icon={UserPlus}
                    label="Pending onboarding"
                    count={pending}
                    sub="Crew/contractors not yet complete"
                    link="/team"
                    testId="compliance-pending-onboarding"
                />
            </div>
        </section>
    );
}

// Keep ShieldCheck imported so designers can use the all-clear variant later
void ShieldCheck;
